import styles from './Shifts.module.css';

import React from 'react';

import Alert from '@mui/material/Alert';
import CircularProgress from '@mui/material/CircularProgress';
import Icon from '@mui/material/Icon';
import Pagination from '@mui/material/Pagination';
import Typography from '@mui/material/Typography';

import CalendarMonth from '@mui/icons-material/CalendarMonth';

import { DateTime } from 'luxon';

import { getShifts, getShift } from "../../api.js";

import { useRealtimeTrigger } from '../../hooks/useRealtimeTrigger';

import { useAuth } from "../../AuthContext";
import { Filter } from '../Filter/Filter';
import { Shift } from '../Shift/Shift';

// MARK: ShiftHeader
const ShiftHeader = ({ page, onSetPage, paginationInfo }) => {
    return (
        <div className={ styles.header }>
            <span>
                <Icon component={ CalendarMonth } sx={ { mr: 1 } } />
                <Typography>Available Shifts</Typography>
            </span>

            { paginationInfo.totalPages > 1 && <Pagination
                size='small'
                color="primary"
                siblingCount={ 0 }
                count={ paginationInfo.totalPages }
                page={ page }
                onChange={ (event, value) => onSetPage(value) }
            /> }
        </div>
    );
};

// MARK: ShiftList
const ShiftList = ({ shifts, patroller, onViewShift, onAssignDuty }) => {
    return (
        <div className={ styles.shifts }>
            { shifts.map(f => {
                return (
                    <Shift
                        key={ f.id }
                        shift={ f }
                        patroller={ patroller }
                        assigned={ patroller.shifts?.find(s => s.id === f.id) }
                        onViewShift={ onViewShift }
                        onAssignDuty={ onAssignDuty }
                    />
                );
            }) }
        </div>
    );
};

// MARK: Shifts
export const Shifts = ({ patroller, size = 10, onViewShift, onAssignDuty }) => {
    const [ shifts, setShifts ] = React.useState([]);
    const [ page, setPage ] = React.useState(1);
    const [ loading, setLoading ] = React.useState(true);
    const [ error, setError ] = React.useState(null);
    const [ filters, setFilters ] = React.useState({
        search: '',
        excludeSearchParams: false,
        days: [],
        startDate: null,
        endDate: null,
        onlyAvailable: false,
        onlyShift: false,
    });

    const { token } = useAuth();

    const pageSize = (Math.min(size, 10) - 1);

    // Filter logic using Data Normalization pattern
    const filteredShifts = React.useMemo(() => {
        return shifts.filter(shift => {
            let include = true;

            // Search filter (name, event, or time)
            if (filters.search) {
                const searchLower = filters.search.toLowerCase();

                // check event name
                const matchesEventShortName = shift.event?.nameShort?.toLowerCase().includes(searchLower);
                const matchesEventLongName = shift.event?.nameLong?.toLowerCase().includes(searchLower);

                // Check shift name
                const matchesName = shift.name?.toLowerCase().includes(searchLower);

                // Check shift time
                const shiftStartTime = DateTime.fromMillis(shift.start).toFormat('HH:mm');
                const shiftEndTime = DateTime.fromMillis(shift.end).toFormat('HH:mm');
                const matchesTime = shiftStartTime.includes(searchLower) || shiftEndTime.includes(searchLower);

                // Check shift date (full date, short date, or month)
                const shiftDateFull = DateTime.fromMillis(shift.start).toFormat('MMMM dd, yyyy').toLowerCase();
                const shiftDateShort = DateTime.fromMillis(shift.start).toFormat('MM/dd/yyyy').toLowerCase();
                const shiftDateMonth = DateTime.fromMillis(shift.start).toFormat('MMMM').toLowerCase();
                const matchesDate = shiftDateFull.includes(searchLower) || shiftDateShort.includes(searchLower) || shiftDateMonth.includes(searchLower);

                const matches = filters.onlyShift ? matchesName : matchesEventShortName || matchesEventLongName || matchesName || matchesTime || matchesDate;

                if (filters.excludeSearchParams) {
                    if (matches) include = false; // exclude if matches
                } else {
                    if (!matches) include = false; // include only if matches
                }
            }

            // Date range filter
            if ((filters.startDate || filters.endDate) && include) {
                const shiftDate = DateTime.fromMillis(shift.start).startOf('day');
                const start = filters.startDate ? DateTime.fromISO(filters.startDate).startOf('day') : null;
                const end = filters.endDate ? DateTime.fromISO(filters.endDate).endOf('day') : null;

                const inRange = (!start || shiftDate >= start) && (!end || shiftDate <= end);

                if (filters.excludeSearchParams) {
                    if (inRange) include = false; // exclude if in range
                } else {
                    if (!inRange) include = false; // include only if in range
                }
            }

            // Day of week filter
            if (filters.days.length > 0 && include) {
                const shiftDay = DateTime.fromMillis(shift.start).toFormat('cccc').toLowerCase();
                const inDays = filters.days.includes(shiftDay);

                if (filters.excludeSearchParams) {
                    if (inDays) include = false; // exclude if in selected days
                } else {
                    if (!inDays) include = false; // include only if in selected days
                }
            }

            // Only Available filter
            if (filters.onlyAvailable && include) {
                const hasAvailableSlots = shift.available > 0;

                if (filters.excludeSearchParams) {
                    if (hasAvailableSlots) include = false; // exclude if available
                } else {
                    if (!hasAvailableSlots) include = false; // include only if available
                }
            }

            return include;
        });
    }, [ shifts, filters ]);

    const currentPageData = React.useMemo(() => {
        const pageIndex = page - 1;

        return filteredShifts.slice(pageIndex * pageSize, (pageIndex * pageSize) + pageSize);
    }, [ filteredShifts, page, pageSize ]);

    // Memoize pagination helpers
    const paginationInfo = React.useMemo(() => {
        const totalPages = Math.ceil(filteredShifts.length / pageSize);

        return { totalPages };
    }, [ filteredShifts.length, pageSize ]);

    const handleShiftUpdate = React.useCallback(async (shiftId) => {
        try {
            const shift = await getShift(shiftId, token);

            if (shift && !shift.error) {
                setShifts(prev => {
                    const index = prev.findIndex(s => s.id === shiftId);

                    if (index !== -1) {
                        const updatedShifts = [ ...prev ];

                        updatedShifts[ index ] = shift;

                        return updatedShifts;
                    }

                    const insertAt = prev.findIndex(s => s.start > shift.start);

                    return insertAt === -1
                        ? [ ...prev, shift ]
                        : [ ...prev.slice(0, insertAt), shift, ...prev.slice(insertAt) ];
                });
            } else {
                console.error('Invalid shift data or error:', shift);
            }
        } catch (error) {
            console.error('Failed to update shift:', error);
        }
    }, [ token ]);

    useRealtimeTrigger('manager', 'shifts', handleShiftUpdate);
    useRealtimeTrigger('scheduler', 'shifts', handleShiftUpdate);

    React.useEffect(() => {
        const fetchData = async () => {
            try {
                setLoading(true);

                const shiftsData = await getShifts(token);

                if (shiftsData.error) {
                    setError('Failed to fetch shifts. Please try again.');

                    return;
                }

                const now = DateTime.now().toLocal().toMillis();
                const dateIndex = shiftsData.findIndex(d => DateTime.fromMillis(d.start).toLocal().toMillis() >= now);
                const initialPage = dateIndex > 0 ? Math.floor(dateIndex / pageSize) + 1 : 1;
  
                setShifts(shiftsData);
                setPage(initialPage);
            } catch (err) {
                setError(err.message);
            } finally {
                setLoading(false);
            }
        };

        fetchData();
    }, [ token, pageSize ]);

    const handleFilterChange = (newFilters) => {
        setFilters(newFilters);
        setPage(1); // Reset to first page when filters change
    };

    const handleClearFilters = () => {
        setFilters({ search: '', days: [], onlyAvailable: false, onlyShift: false });
        setPage(1);
    };

    if (loading) {
        return <CircularProgress sx={ { mb: 2 } } />;
    }

    if (error) {
        return <Alert variant="filled" severity="error">{ error }</Alert>;
    }

    return (
        <>
            <Filter
                filters={ filters }
                onFilterChange={ handleFilterChange }
                onClearFilters={ handleClearFilters }
            />

            <div className={ styles.shiftContainer }>
                <ShiftHeader
                    shiftCount={ filteredShifts.length }
                    page={ page }
                    onSetPage={ setPage }
                    paginationInfo={ paginationInfo }
                />

                <ShiftList
                    shifts={ currentPageData }
                    patroller={ patroller }
                    onViewShift={ onViewShift }
                    onAssignDuty={ onAssignDuty }
                />

                { paginationInfo.totalPages > 1 && <div className={ styles.footer }>
                    <Pagination
                        color="primary"
                        count={ paginationInfo.totalPages }
                        page={ page }
                        onChange={ (event, value) => setPage(value) }
                    />
                </div> }
            </div>
        </>
    );
};